import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Warning, X } from "@phosphor-icons/react";

/**
 * v1.25.59 — Impossible-to-miss red banner across the top of every admin
 * page whenever the deployment's VAPID env vars are missing. Without them,
 * customers CANNOT receive OS-level push notifications on their phones,
 * no matter how many times they tap "Turn on notifications" — so calling
 * this out loudly saves the admin hours of confusion.
 *
 * Silently self-hides when Web Push is configured, so it never shows on a
 * healthy deployment. Dismissible per browser (24h) — after that it comes
 * back until the problem is actually fixed.
 */
const SNOOZE_KEY = "micm_push_health_dismissed_at";
const SNOOZE_MS = 24 * 60 * 60 * 1000;

export default function PushHealthBanner() {
    const [info, setInfo] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/health/push");
                if (!cancelled) setInfo(r.data);
            } catch {
                // Endpoint not deployed yet — stay quiet.
            }
        })();
        try {
            const at = Number(localStorage.getItem(SNOOZE_KEY) || 0);
            if (at && Date.now() - at < SNOOZE_MS) setDismissed(true);
        } catch { /* noop */ }
        return () => { cancelled = true; };
    }, []);

    if (!info || info.configured || dismissed) return null;
    const missing = (info.missing_env_vars || []).join(", ") || "VAPID keys";

    const snooze = () => {
        try { localStorage.setItem(SNOOZE_KEY, String(Date.now())); } catch { /* noop */ }
        setDismissed(true);
    };

    return (
        <div
            data-testid="push-health-banner"
            className="sticky top-0 z-40 flex items-start justify-between gap-3 border-b-2 border-rose-500 bg-gradient-to-r from-rose-600 to-red-700 px-4 py-2 text-white shadow-md sm:px-6"
        >
            <div className="flex items-start gap-2.5 min-w-0">
                <Warning size={18} weight="fill" className="mt-0.5 shrink-0 text-amber-300 animate-pulse" />
                <div className="min-w-0 text-xs leading-relaxed">
                    <div className="font-bold uppercase tracking-wider">
                        Push notifications are DISABLED on this server
                    </div>
                    <div className="mt-0.5 text-white/95">
                        Customers cannot receive Thank-You alerts on their phone lock screen. Missing env vars:&nbsp;
                        <code className="rounded bg-black/25 px-1 py-0.5 font-mono text-[10px] font-semibold">{missing}</code>.
                        Add them to your deployment (+ <code className="rounded bg-black/25 px-1 py-0.5 font-mono text-[10px]">REACT_APP_VAPID_PUBLIC_KEY</code> on the frontend) and redeploy.
                        {" "}Test any time: <code className="rounded bg-black/25 px-1 py-0.5 font-mono text-[10px]">GET /api/health/push</code>.
                    </div>
                </div>
            </div>
            <button
                type="button"
                aria-label="Dismiss for 24 hours"
                onClick={snooze}
                className="shrink-0 rounded-full p-1 text-white/80 hover:bg-white/10 hover:text-white"
            >
                <X size={14} weight="bold" />
            </button>
        </div>
    );
}
